import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import notificationIcon from '@/assets/diy/notification-icon.svg';
import notificationBanner from '@/assets/diy/notification-banner.svg';

import { join_room, leave_room } from '@/actions';
import { emitterEvent } from '@/Constant';
import EventEmitter from '@/utils/eventEmitter';
import { pinRoom, unpinRoom } from '@/utils/pinnedRooms';
import { navigateToTask } from '@/utils/diyDollGroupNavigation';
import { getTeamActivityInstance } from '@/services/diyDollGroupApi';

import './index.less';

// diy-doll-group-wish is a group task page registered outside the /diy-doll-group/ prefix
const DIY_DOLL_GROUP_PATH = /^\/client\/diy-doll-group(-wish)?(\/|$)/;

/**
 * App-level dialog shown on every diy-doll-group page when the host unlocks a new task.
 * "继续" opens the task named in the unlock event, "暂不" just closes the dialog.
 */
export default function TaskUnlockDialog() {
  const teamActivityInstanceId = useSelector(state => state.baseInfo.teamActivityInstanceId);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dispatch = useDispatch();

  const [task, setTask] = useState(null);
  const latestEventRef = useRef(0);

  const isActive = DIY_DOLL_GROUP_PATH.test(pathname) && !!teamActivityInstanceId;

  // Stay in the team room on every page of the flow, even those that never join it
  // (e.g. the create-doll chain) or that leave it when they unmount
  useEffect(() => {
    if (!isActive) return;
    const roomId = `ai_team:${teamActivityInstanceId}`;
    pinRoom(roomId);
    dispatch(join_room(roomId));
    return () => {
      unpinRoom(roomId);
      dispatch(leave_room(roomId));
    };
  }, [dispatch, isActive, teamActivityInstanceId]);

  useEffect(() => {
    // Runs when entering/leaving the flow or switching activity: drop any dialog from before
    setTask(null);
    if (!isActive) return;

    let cancelled = false;

    async function handleTaskUnlocked(messageObj = {}) {
      const { teamActivityInstanceId: tid, message, taskUrl, taskItemId } = messageObj;
      if (tid && String(tid) !== String(teamActivityInstanceId)) return;

      const eventNo = ++latestEventRef.current;
      let matchedItem;
      try {
        const res = await getTeamActivityInstance(tid || teamActivityInstanceId);
        matchedItem = res?.result?.taskItems?.find(t => t.taskItemId === taskItemId);
      } catch (error) {
        console.error('TaskUnlockDialog: fetch task list error', error);
      }
      // Effect was torn down (left the flow / switched activity) or a newer unlock arrived
      if (cancelled || eventNo !== latestEventRef.current) return;

      const url = taskUrl || matchedItem?.taskUrl;
      const name = matchedItem?.taskItemName || message;
      if (!url && !name) return;

      setTask({ name, url, item: matchedItem || {} });
    }

    EventEmitter.on(emitterEvent.TEAM_ACTIVITY_TODO_UNLOCK, handleTaskUnlocked);
    return () => {
      EventEmitter.removeListener(emitterEvent.TEAM_ACTIVITY_TODO_UNLOCK, handleTaskUnlocked);
      // Invalidate any in-flight request so it can't repopulate the dialog afterwards
      cancelled = true;
    };
  }, [isActive, teamActivityInstanceId]);

  if (!isActive || !task) return null;

  function handleNext() {
    const { url, item } = task;
    setTask(null);
    if (url) navigateToTask(navigate, url, item);
  }

  return (
    <div className="task-unlock-dialog" role="dialog" aria-modal="true">
      <div className="task-unlock-dialog__mask" />
      <div className="task-unlock-dialog__card">
        <img src={notificationBanner} alt="" className="task-unlock-dialog__bg" />
        <div className="task-unlock-dialog__content">
          <div className="task-unlock-dialog__header">
            <img src={notificationIcon} alt="" className="task-unlock-dialog__icon" />
            <span className="task-unlock-dialog__title">您有一个新任务</span>
          </div>
          <div className="task-unlock-dialog__task-name">{task.name}</div>
          <div className="task-unlock-dialog__actions">
            <button
              type="button"
              className="task-unlock-dialog__btn task-unlock-dialog__btn--secondary"
              onClick={() => setTask(null)}
            >
              暂不
            </button>
            <button
              type="button"
              className="task-unlock-dialog__btn task-unlock-dialog__btn--primary"
              onClick={handleNext}
            >
              继续
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
